import React, { useState } from "react";
import { createPortal } from "react-dom";
import { supabase } from "../../supabaseClient";
import "./PaymentUploadModal.css";

const ADMIN_PAYMENTS_EMAIL = "support@bizbazevents.com";
const ADMIN_PAYMENTS_CC = ["info@bizbazevents.com", "support@bizbazevents.com", "accounts@bizbazevents.com"];
const DOCS_BUCKET = "Vendor_onboarding_forms";

const TYPE_LABELS = {
  deposit: "Deposit",
  deposit_topup: "Remaining Deposit",
  balance: "Balance",
  full: "Full Payment",
};

function buildPaymentNotificationEmailHtml({ brandName, eventName, typeLabel, invoiceTotal, requiredAmount, declaredAmount }) {
  return `
<!DOCTYPE html>
<html>
<body style="margin:0;padding:0;background-color:#f4f5f7;font-family:Arial,Helvetica,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#f4f5f7;padding:40px 16px;">
    <tr><td align="center">
      <table width="600" cellpadding="0" cellspacing="0" border="0" style="max-width:600px;width:100%;background-color:#ffffff;border-radius:12px;overflow:hidden;border:1px solid #e8eaed;">
        <tr><td style="height:4px;background-color:#2563eb;font-size:0;line-height:0;">&nbsp;</td></tr>
        <tr><td style="padding:28px 32px;">
          <p style="margin:0 0 4px;font-size:12px;color:#6b7280;text-transform:uppercase;letter-spacing:0.04em;">Payment submitted</p>
          <p style="margin:0 0 20px;font-size:18px;font-weight:800;color:#111827;">${brandName} — ${typeLabel}</p>

          <table width="100%" cellpadding="0" cellspacing="0" border="0" style="margin-bottom:20px;">
            <tr><td style="padding:6px 0;font-size:14px;color:#6b7280;">Event</td><td align="right" style="padding:6px 0;font-size:14px;font-weight:600;color:#111827;">${eventName || "—"}</td></tr>
            <tr><td style="padding:6px 0;font-size:14px;color:#6b7280;">Invoice total</td><td align="right" style="padding:6px 0;font-size:14px;font-weight:600;color:#111827;">KES ${Number(invoiceTotal || 0).toLocaleString()}</td></tr>
            <tr><td style="padding:6px 0;font-size:14px;color:#6b7280;">Required amount</td><td align="right" style="padding:6px 0;font-size:14px;font-weight:600;color:#111827;">KES ${Number(requiredAmount || 0).toLocaleString()}</td></tr>
            <tr><td style="padding:6px 0;font-size:14px;color:#6b7280;">Declared amount</td><td align="right" style="padding:6px 0;font-size:14px;font-weight:700;color:#16a34a;">KES ${Number(declaredAmount || 0).toLocaleString()}</td></tr>
          </table>

          <p style="margin:0;font-size:13px;color:#6b7280;">
            Payment proof is attached to this email.
          </p>
        </td></tr>
      </table>
    </td></tr>
  </table>
</body>
</html>
  `;
}

const TERMS = [
  "I declare the amount entered is accurate.",
  "The uploaded payment proof is genuine.",
  "I understand misleading or falsified payment proof may result in application cancellation and restriction from future events.",
  `If I require alternative payment arrangements, I must contact ${ADMIN_PAYMENTS_EMAIL} before making payment.`,
];

// Read a File as a base64 string (no "data:...;base64," prefix) for use as
// an email attachment.
const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = () => reject(new Error("Failed to read file for attachment"));
    reader.readAsDataURL(file);
  });

/**
 * @param {"deposit"|"deposit_topup"|"balance"|"full"} type
 * @param {number} requiredAmount - minimum declared amount for this submission
 * @param {number} invoiceTotal
 *
 * "deposit_topup" covers the case where an admin corrected the vendor's
 * tent_type/price AFTER a deposit was already declared, and the original
 * declared amount no longer meets the recalculated deposit requirement.
 * It writes to separate deposit_topup_* columns rather than overwriting the
 * original deposit_declared_amount, so the vendor's original submission
 * stays intact and InvoiceTab can sum original + top-up to get what's
 * actually been paid. Submitting a top-up resets deposit_confirmed to
 * false, since the combined amount hasn't been verified yet.
 */
const PaymentUploadModal = ({ type, requiredAmount, invoiceTotal, application, vendor, event, onSuccess, onClose }) => {
  const [file, setFile] = useState(null);
  const [declaredAmount, setDeclaredAmount] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const typeLabel = TYPE_LABELS[type];
  const parsedAmount = parseFloat(declaredAmount);
  const amountTooLow = declaredAmount !== "" && !isNaN(parsedAmount) && parsedAmount < requiredAmount;
  const canSubmit = file && declaredAmount !== "" && !isNaN(parsedAmount) && !amountTooLow && agreed && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");

    try {
      const ext = file.name.split(".").pop();
      const path = `payments/${application.id}/${type}_${Date.now()}.${ext}`;

      const { error: uploadErr } = await supabase.storage.from(DOCS_BUCKET).upload(path, file, { upsert: true });
      if (uploadErr) throw new Error("Upload failed: " + uploadErr.message);

      const { data: urlData } = supabase.storage.from(DOCS_BUCKET).getPublicUrl(path);
      const proofUrl = urlData.publicUrl;
      const now = new Date().toISOString();

      let updateFields;
      if (type === "deposit") {
        updateFields = {
          deposit_submitted: true,
          deposit_proof_url: proofUrl,
          deposit_declared_amount: parsedAmount,
          deposit_declared_at: now,
        };
      } else if (type === "deposit_topup") {
        updateFields = {
          deposit_topup_proof_url: proofUrl,
          deposit_topup_declared_amount: parsedAmount,
          deposit_topup_declared_at: now,
          // combined amount (original + top-up) hasn't been verified yet
          deposit_confirmed: false,
        };
      } else if (type === "balance") {
        updateFields = {
          balance_submitted: true,
          balance_proof_url: proofUrl,
          balance_declared_amount: parsedAmount,
          balance_declared_at: now,
        };
      } else {
        // full — covers both stages at once
        updateFields = {
          deposit_submitted: true,
          deposit_proof_url: proofUrl,
          deposit_declared_amount: parsedAmount,
          deposit_declared_at: now,
          balance_submitted: true,
          balance_declared_amount: parsedAmount,
          balance_declared_at: now,
        };
      }

      const { error: updateErr } = await supabase
        .from("vendor_applications")
        .update(updateFields)
        .eq("id", application.id);
      if (updateErr) throw updateErr;

      // ── Admin notification — proof goes as an attachment, cc'd to the team ──
      try {
        const attachmentContent = await fileToBase64(file);

        const res = await fetch("/api/send-email", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            to: ADMIN_PAYMENTS_EMAIL,
            cc: ADMIN_PAYMENTS_CC,
            subject: `Payment submitted — ${vendor.brand_name} (${typeLabel})`,
            html: buildPaymentNotificationEmailHtml({
              brandName: vendor.brand_name,
              eventName: event?.name,
              typeLabel,
              invoiceTotal,
              requiredAmount,
              declaredAmount: parsedAmount,
            }),
            attachments: [
              {
                filename: file.name,
                content: attachmentContent,
              },
            ],
          }),
        });
        const data = await res.json();
        console.log("[PaymentUploadModal] email response:", res.status, data);
        if (!res.ok) console.error("[PaymentUploadModal] email send failed:", data);
      } catch (emailErr) {
        // Non-fatal — payment already saved, just log
        console.error("[PaymentUploadModal] email fetch error:", emailErr);
      }

      onSuccess(updateFields);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div className="pum-overlay" onClick={onClose}>
      <div className="pum-modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="pum-title">Upload {typeLabel}</h3>
        <p className="pum-sub">
          {type === "deposit_topup"
            ? `Additional amount needed: KES ${requiredAmount.toLocaleString()}`
            : `Required: KES ${requiredAmount.toLocaleString()}`}
        </p>

        <label className="pum-label">Payment proof</label>
        <input
          type="file"
          accept="image/*,application/pdf"
          onChange={(e) => setFile(e.target.files[0])}
          className="pum-file-input"
        />

        <label className="pum-label">Declared amount paid (KES)</label>
        <input
          type="number"
          min="0"
          value={declaredAmount}
          onChange={(e) => setDeclaredAmount(e.target.value)}
          placeholder={`At least ${requiredAmount.toLocaleString()}`}
          className={`pum-amount-input ${amountTooLow ? "pum-amount-input--error" : ""}`}
        />
        {amountTooLow && (
          <p className="pum-field-error">
            Declared amount must be at least KES {requiredAmount.toLocaleString()}.
          </p>
        )}

        <div className="pum-terms">
          <p className="pum-terms-label">Before you submit</p>
          <ul>
            {TERMS.map((term, i) => <li key={i}>{term}</li>)}
          </ul>
        </div>

        <label className="pum-checkbox-label">
          <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
          I agree to all of the above
        </label>

        {error && <p className="pum-error">{error}</p>}

        <div className="pum-actions">
          <button className="pum-btn pum-btn--ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button className="pum-btn pum-btn--primary" onClick={handleSubmit} disabled={!canSubmit}>
            {submitting ? "Submitting…" : "Submit for verification"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PaymentUploadModal;